import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { MUNILY_IMAGE_PREVIEW_ICON_ACTION_DEFAULTS, MunilyImagePreviewIconActionSize } from './image-preview-icon-action.types';

@Component({
  selector: 'common-image-preview-icon-action',
  templateUrl: './image-preview-icon-action.component.html',
  styleUrls: ['./image-preview-icon-action.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImagePreviewIconActionComponent implements OnChanges {
  @Input() imageUrl: string | undefined = MUNILY_IMAGE_PREVIEW_ICON_ACTION_DEFAULTS.imageUrl;
  @Input() icon: string | undefined = MUNILY_IMAGE_PREVIEW_ICON_ACTION_DEFAULTS.icon;
  @Input() iconSet: string = MUNILY_IMAGE_PREVIEW_ICON_ACTION_DEFAULTS.iconSet;
  @Input() alt = MUNILY_IMAGE_PREVIEW_ICON_ACTION_DEFAULTS.alt;
  @Input() disabled = MUNILY_IMAGE_PREVIEW_ICON_ACTION_DEFAULTS.disabled;
  @Input() size: MunilyImagePreviewIconActionSize = MUNILY_IMAGE_PREVIEW_ICON_ACTION_DEFAULTS.size;
  /** Marca visual tipo "selected" + check_circle (ej. portada seleccionada). */
  @Input() selected: boolean = false;

  /** When false, hides the overflow action button (read-only preview). */
  @Input() showAction: boolean = MUNILY_IMAGE_PREVIEW_ICON_ACTION_DEFAULTS.showAction;

  /** Ligature del `common-icon-button` (p. ej. edit, more_vert). */
  @Input() actionIcon = MUNILY_IMAGE_PREVIEW_ICON_ACTION_DEFAULTS.actionIcon;
  /** Texto accesible del botón de acción. */
  @Input() actionAriaLabel = MUNILY_IMAGE_PREVIEW_ICON_ACTION_DEFAULTS.actionAriaLabel;

  @Input() showReplaceOption: boolean = MUNILY_IMAGE_PREVIEW_ICON_ACTION_DEFAULTS.showReplaceOption;
  @Input() showSetCoverOption: boolean = MUNILY_IMAGE_PREVIEW_ICON_ACTION_DEFAULTS.showSetCoverOption;
  @Input() showDeleteOption: boolean = MUNILY_IMAGE_PREVIEW_ICON_ACTION_DEFAULTS.showDeleteOption;

  @Output() action = new EventEmitter<void>();
  @Output() menuAction = new EventEmitter<'replace' | 'setCover' | 'delete'>();

  public hostClasses: string[] = [];

  constructor(private readonly sanitizer: DomSanitizer) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['disabled'] ||
      changes['size'] ||
      changes['imageUrl'] ||
      changes['icon'] ||
      changes['selected'] ||
      changes['showAction']
    ) {
      this.syncHostClasses();
    }
  }

  get effectiveSelected(): boolean {
    if (!this.selected) return false;
    return this.hasImage || this.hasIcon;
  }

  get hasImage(): boolean {
    return !!this.imageUrl?.trim();
  }

  get hasIcon(): boolean {
    return !this.hasImage && !!this.icon?.trim();
  }

  get safeImageSrc(): string | SafeUrl {
    const url = this.imageUrl;
    if (!url) {
      return '';
    }
    if (url.startsWith('blob:') || url.startsWith('data:')) {
      return this.sanitizer.bypassSecurityTrustUrl(url);
    }
    return url;
  }

  onAction(): void {
    if (!this.disabled) {
      this.action.emit();
    }
  }

  onMenuAction(action: 'replace' | 'setCover' | 'delete'): void {
    if (!this.disabled) {
      this.menuAction.emit(action);
    }
  }

  private syncHostClasses(): void {
    const next: string[] = [];
    if (this.disabled) next.push('munily-image-preview-ia--disabled');
    if (this.size === 'large') next.push('munily-image-preview-ia--large');
    if (this.size === 'xsmall') next.push('munily-image-preview-ia--xsmall');
    if (this.hasImage) next.push('munily-image-preview-ia--has-image');
    if (this.effectiveSelected) next.push('munily-image-preview-ia--selected');
    if (!this.showAction) next.push('munily-image-preview-ia--no-action');
    this.hostClasses = next;
  }
}
